import { useTranslation } from 'react-i18next';
import {
  Grid,
  Box,
  Typography,
  Alert,
  AlertTitle,
  MenuItem,
  Select,
  FormControl,
  Chip,
  ListSubheader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { CheckCircle, ErrorOutline, Label, RemoveCircleOutline } from '@mui/icons-material';
import { importFieldDefinitions, guessFieldMapping, ImportFieldDefinition } from '../models/importFieldDefinitions';

export const PROFILE_PREFIX = 'profile:';

export interface ColumnMapping {
  /** Kolumnnamn från Excel-filen */
  excelHeader: string;
  /** Mappat fältkey, eller null om ej mappad. Profile-kolumner har prefix "profile:" */
  fieldKey: string | null;
}

interface MemberImportColumnMappingProps {
  excelHeaders: string[];
  mappings: ColumnMapping[];
  onMappingsChange: (mappings: ColumnMapping[]) => void;
  previewRows: Record<string, string | number>[];
}

/** Returnerar initiala mappningar baserat på synonym-matchning.
 *  Kolumner som inte matchar något standardfält behandlas som profilkategorier.
 */
export const createInitialMappings = (excelHeaders: string[]): ColumnMapping[] =>
  excelHeaders.map((header) => {
    const fieldKey = guessFieldMapping(header);
    if (fieldKey) return { excelHeader: header, fieldKey };
    // Omatchade kolumner → behandla som profilkategori
    return { excelHeader: header, fieldKey: `${PROFILE_PREFIX}${header}` };
  });

/** Kontrollera om en mappning är en profilkategori */
export const isProfileMapping = (fieldKey: string | null): boolean => !!fieldKey && fieldKey.startsWith(PROFILE_PREFIX);

/** Hämta kategorinamnet från en profile-mappning */
export const getProfileCategory = (fieldKey: string): string => fieldKey.replace(PROFILE_PREFIX, '');

export const MemberImportColumnMapping = ({ excelHeaders, mappings, onMappingsChange, previewRows }: MemberImportColumnMappingProps) => {
  const { t } = useTranslation(['member', 'common']);

  const standardFields = importFieldDefinitions;

  // Vilka fält är redan mappade (exkl. profile-kolumner, de tillåter dubbletter)
  const usedFieldKeys = new Set(mappings.filter((m) => m.fieldKey && !isProfileMapping(m.fieldKey)).map((m) => m.fieldKey));

  // Obligatoriska fält som saknar mappning
  const requiredFields = importFieldDefinitions.filter((f) => f.required);
  const allMappedKeys = new Set(mappings.filter((m) => m.fieldKey).map((m) => m.fieldKey));
  const unmappedRequired = requiredFields.filter((f) => !allMappedKeys.has(f.key));

  const handleFieldChange = (excelHeader: string, newFieldKey: string | null) => {
    let resolvedKey = newFieldKey;

    // Om användaren väljer "profileCategory" → skapa profile:KolumnNamn
    if (newFieldKey === '__profileCategory__') {
      resolvedKey = `${PROFILE_PREFIX}${excelHeader}`;
    }

    const updated = mappings.map((m) => (m.excelHeader === excelHeader ? { ...m, fieldKey: resolvedKey } : m));
    onMappingsChange(updated);
  };

  const getFieldDef = (key: string): ImportFieldDefinition | undefined => importFieldDefinitions.find((f) => f.key === key);

  const getPreviewValues = (header: string): string[] => {
    const values: string[] = [];
    for (let i = 0; i < Math.min(3, previewRows.length); i += 1) {
      const val = String(previewRows[i][header] ?? '').trim();
      if (val) values.push(val);
    }
    return values;
  };

  const getSelectValue = (fieldKey: string | null): string => {
    if (!fieldKey) return '';
    if (isProfileMapping(fieldKey)) return '__profileCategory__';
    return fieldKey;
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography gutterBottom variant='h6'>
          {t('member:importMember.mappingHeader')}
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          {t('member:importMember.mappingDescription')}
        </Typography>
      </Grid>

      {unmappedRequired.length > 0 && (
        <Grid item xs={12}>
          <Alert severity='warning'>
            <AlertTitle>{t('member:importMember.requiredFieldsMissing')}</AlertTitle>
            {unmappedRequired.map((f) => f.label).join(', ')}
          </Alert>
        </Grid>
      )}

      <Grid item xs={12}>
        <TableContainer sx={{ maxHeight: 500 }}>
          <Table size='small' stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: '25%' }}>{t('member:importMember.mappingExcelColumn')}</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '25%' }}>{t('member:importMember.mappingPreview')}</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '25%' }}>{t('member:importMember.mappingSystemField')}</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '25%' }}>{t('member:importMember.mappingStatus')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {excelHeaders.map((header) => {
                const mapping = mappings.find((m) => m.excelHeader === header);
                const fieldKey = mapping?.fieldKey || null;
                const fieldDef = fieldKey && !isProfileMapping(fieldKey) ? getFieldDef(fieldKey) : null;
                const isProfile = isProfileMapping(fieldKey);
                const preview = getPreviewValues(header);
                const isRequired = fieldDef?.required ?? false;

                return (
                  <TableRow key={header} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell>
                      <Typography variant='body2' sx={{ fontWeight: 500 }}>
                        {header}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                        {preview.map((val) => (
                          <Typography key={`${header}-${val}`} variant='caption' color='text.secondary' noWrap sx={{ maxWidth: 200 }}>
                            {val}
                          </Typography>
                        ))}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <FormControl size='small' fullWidth>
                        <Select
                          value={getSelectValue(fieldKey)}
                          onChange={(e) => handleFieldChange(header, e.target.value || null)}
                          displayEmpty
                          sx={{ fontSize: '0.8rem' }}
                        >
                          <MenuItem value=''>
                            <em>{t('member:importMember.mappingIgnoreColumn')}</em>
                          </MenuItem>

                          <ListSubheader>{t('member:importMember.mappingStandardFields')}</ListSubheader>
                          {standardFields.map((def) => {
                            const isUsedByOther = usedFieldKeys.has(def.key) && fieldKey !== def.key;
                            return (
                              <MenuItem key={def.key} value={def.key} disabled={isUsedByOther}>
                                {def.label}
                                {def.required ? ' *' : ''}
                                {def.description ? ` (${def.description})` : ''}
                              </MenuItem>
                            );
                          })}

                          <ListSubheader>{t('member:importMember.mappingProfileFields')}</ListSubheader>
                          <MenuItem value='__profileCategory__'>
                            {t('member:importMember.mappingProfileCategory')} ({header})
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>

                    <TableCell>
                      {isProfile && (
                        <Chip
                          size='small'
                          icon={<Label fontSize='small' />}
                          label={`${t('member:importMember.mappingProfile')}: ${getProfileCategory(fieldKey as string)}`}
                          color='info'
                          variant='outlined'
                        />
                      )}
                      {fieldKey === 'profiles' &&
                        (() => {
                          const groups = new Set<string>();
                          previewRows.forEach((row) => {
                            const val = String(row[header] ?? '');
                            val.split(',').forEach((pair) => {
                              const trimmed = pair.trim();
                              if (trimmed.includes('|')) groups.add(trimmed.split('|')[0].trim());
                            });
                          });
                          return groups.size > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {Array.from(groups).map((g) => (
                                <Chip
                                  key={g}
                                  size='small'
                                  icon={<Label fontSize='small' />}
                                  label={`${t('member:importMember.mappingProfile')}: ${g}`}
                                  color='info'
                                  variant='outlined'
                                />
                              ))}
                            </Box>
                          ) : (
                            <CheckCircle fontSize='small' color='success' />
                          );
                        })()}
                      {fieldKey && !isProfile && fieldKey !== 'profiles' && <CheckCircle fontSize='small' color='success' />}
                      {!fieldKey && (
                        <Chip
                          size='small'
                          icon={<RemoveCircleOutline fontSize='small' />}
                          label={t('member:importMember.mappingIgnored')}
                          variant='outlined'
                          color='default'
                        />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>

      <Grid item xs={12}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Chip
            icon={<CheckCircle />}
            label={`${mappings.filter((m) => m.fieldKey && !isProfileMapping(m.fieldKey)).length} ${t('member:importMember.mappingMappedCount')}`}
            color='success'
            variant='outlined'
            size='small'
          />
          {mappings.filter((m) => isProfileMapping(m.fieldKey)).length > 0 && (
            <Chip
              icon={<Label />}
              label={`${mappings.filter((m) => isProfileMapping(m.fieldKey)).length} ${t('member:importMember.mappingProfileCount')}`}
              color='info'
              variant='outlined'
              size='small'
            />
          )}
          <Chip
            icon={<RemoveCircleOutline />}
            label={`${mappings.filter((m) => !m.fieldKey).length} ${t('member:importMember.mappingIgnoredCount')}`}
            variant='outlined'
            size='small'
          />
          {unmappedRequired.length > 0 && (
            <Chip
              icon={<ErrorOutline />}
              label={`${unmappedRequired.length} ${t('member:importMember.mappingRequiredMissing')}`}
              color='error'
              variant='outlined'
              size='small'
            />
          )}
        </Box>
      </Grid>
    </Grid>
  );
};
